import { useState } from 'react';
import { CreatePositionFillRequestDrawer, POSITION_FILL_REQUEST_CREATE } from 'domain/shared/positionFillRequest';
import { useAuthorization } from 'framework/authorization';
import { AddressableViewLayout } from 'framework/ViewTypes';
import { bindCollection } from 'framework/ViewTypes/contentLayout';
import { ErrorMessage, SuccessMessage, useMessageService } from 'framework/ui/Message';
import {
  POSITION_SLOT_ASSIGN,
  POSITION_SLOT_CREATE,
  POSITION_SLOT_FREEZE,
  POSITION_SLOT_RETIRE,
  POSITION_SLOT_UNFREEZE,
  POSITION_SLOT_UPDATE,
} from '../constants';
import { PositionSlotAssignmentDrawer, PositionSlotFormDrawer } from '../createupdate';
import type { PreviewPositionSlotListItem } from '../types';
import usePositionSlotsService from '../usePositionSlotsService';
import { PositionSlotValidationProvider } from './PositionSlotValidationContext';
import { getPositionSlotsTableConfig } from './positionSlotsTableConfig';
import usePositionSlotsCollection from './usePositionSlotsCollection';
import usePositionSlotsFilterBar from './usePositionSlotsFilterBar';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import { usePositionSlotsUrl } from 'domain/shared/positionSlot';

enum ActiveDrawer {
  FORM,
  ASSIGN,
  PFR,
}

const PositionSlotsCollectionPreviewPage = () => {
  const { isGranted } = useAuthorization();
  const { isFeatureEnabled } = useFeatureToggle();
  const { getSingleEntityUrl } = usePositionSlotsUrl();
  const { addMessage } = useMessageService();
  const service = usePositionSlotsService();
  const [activeDrawer, setActiveDrawer] = useState<Nullable<ActiveDrawer>>(null);
  const [selectedPosition, setSelectedPosition] = useState<PreviewPositionSlotListItem>();
  const collection = usePositionSlotsCollection();
  const { searchProps, filterProps } = usePositionSlotsFilterBar(collection);

  const openDrawer = (drawer: Exclude<ActiveDrawer, null>, position?: PreviewPositionSlotListItem) => {
    setSelectedPosition(position);
    setActiveDrawer(drawer);
  };

  const closeDrawer = () => {
    setActiveDrawer(null);
    setSelectedPosition(undefined);
  };

  const runAction = async (action: () => Promise<unknown>, successMessage: string) => {
    try {
      await action();
      addMessage(new SuccessMessage(successMessage));
      await collection.reload();
    } catch {
      addMessage(new ErrorMessage('Unable to update the position.'));
    }
  };

  const drawer = (() => {
    if (activeDrawer === ActiveDrawer.FORM) {
      return (
        <PositionSlotFormDrawer
          isOpen
          onClose={closeDrawer}
          positionSlot={selectedPosition}
          onSuccess={() => collection.reload()}
        />
      );
    }
    if (activeDrawer === ActiveDrawer.PFR && selectedPosition) {
      return (
        <CreatePositionFillRequestDrawer
          isOpen
          positionSlot={selectedPosition}
          onClose={closeDrawer}
          onCreated={() => collection.reload()}
        />
      );
    }
    if (activeDrawer === ActiveDrawer.ASSIGN && selectedPosition) {
      return (
        <PositionSlotAssignmentDrawer
          isOpen
          positionSlot={selectedPosition}
          onClose={closeDrawer}
          onSuccess={() => collection.reload()}
        />
      );
    }
    return undefined;
  })();

  const binding = bindCollection(
    collection,
    getPositionSlotsTableConfig(collection.rows, getSingleEntityUrl, {
      canCreate: isGranted(POSITION_SLOT_CREATE),
      canUpdate: isGranted(POSITION_SLOT_UPDATE),
      canAssign: isGranted(POSITION_SLOT_ASSIGN),
      canFreeze: isGranted(POSITION_SLOT_FREEZE),
      canUnfreeze: isGranted(POSITION_SLOT_UNFREEZE),
      canRetire: isGranted(POSITION_SLOT_RETIRE),
      canCreatePositionFillRequest:
        isGranted(POSITION_FILL_REQUEST_CREATE) && isFeatureEnabled(Features.POSITION_FILL_REQUESTS_COLLECTION),
      onCreate: () => openDrawer(ActiveDrawer.FORM),
      onEdit: position => openDrawer(ActiveDrawer.FORM, position),
      onAssign: position => openDrawer(ActiveDrawer.ASSIGN, position),
      onCreatePositionFillRequest: position => openDrawer(ActiveDrawer.PFR, position),
      onFreeze: position =>
        runAction(() => service.freezePositionSlot(position.id), 'Position has been successfully frozen.'),
      onUnfreeze: position =>
        runAction(() => service.unfreezePositionSlot(position.id), 'Position has been successfully unfrozen.'),
      onRetire: position =>
        runAction(() => service.retirePositionSlot(position.id), 'Position has been successfully retired.'),
    }),
    { searchProps, filterProps },
  );

  return (
    <PositionSlotValidationProvider positionSlots={collection.rows} loading={collection.isLoading}>
      <AddressableViewLayout {...binding.pageProps} drawer={drawer} />
    </PositionSlotValidationProvider>
  );
};

export default PositionSlotsCollectionPreviewPage;
